import * as THREE from 'three';
import { TerrainFeature } from './TerrainFeature';
import { LaunchHillConfig, TerrainBounds, TERRAIN_COLORS } from './TerrainConfig';

/**
 * Launch Hill - Steep asymmetric hill designed for launching bikes into the air.
 * Features a gradual approach slope and steep back for catching air.
 * Height range: 5-15m for Tier 1-2 highway access.
 */
export class LaunchHill extends TerrainFeature {
  private peakHeight: number;
  private length: number;
  private width: number;
  private halfLength: number;
  private halfWidth: number;

  constructor(scene: THREE.Scene, config: LaunchHillConfig) {
    super(scene, config.position, config.rotation || 0, 'launch_hill');

    this.peakHeight = config.peakHeight;
    this.length = config.length;
    this.width = config.width;
    this.halfLength = this.length / 2;
    this.halfWidth = this.width / 2;

    this.createMesh();
    this.addToScene();
  }

  private createMesh(): void {
    // Create asymmetric hill geometry
    // Approach side (front): gradual slope
    // Back side: steep drop for catching air

    const segments = 16;
    const geometry = new THREE.BufferGeometry();
    const vertices: number[] = [];
    const indices: number[] = [];

    // Generate vertices in a grid
    for (let z = 0; z <= segments; z++) {
      for (let x = 0; x <= segments; x++) {
        // Local position (-halfWidth to +halfWidth, -halfLength to +halfLength)
        const localX = (x / segments - 0.5) * this.width;
        const localZ = (z / segments - 0.5) * this.length;

        // Calculate height using asymmetric profile
        const height = this.calculateLocalHeight(localX, localZ);

        vertices.push(localX, height, localZ);
      }
    }

    // Generate indices for triangles
    for (let z = 0; z < segments; z++) {
      for (let x = 0; x < segments; x++) {
        const a = z * (segments + 1) + x;
        const b = a + 1;
        const c = a + (segments + 1);
        const d = c + 1;

        indices.push(a, b, c);
        indices.push(b, d, c);
      }
    }

    geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(vertices, 3)
    );
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    // Main hill mesh
    const material = this.createTerrainMaterial(TERRAIN_COLORS.BASE);
    const hillMesh = new THREE.Mesh(geometry, material);
    this.mesh.add(hillMesh);

    // Add edge highlight at peak
    this.createPeakHighlight();

    // Add grid lines on surface
    this.createGridLines();
  }

  private createPeakHighlight(): void {
    // Create a glowing ring at the peak
    const ringGeometry = new THREE.RingGeometry(0.5, 1.5, 16);
    const ringMaterial = new THREE.MeshStandardMaterial({
      color: TERRAIN_COLORS.EDGE_HIGHLIGHT,
      emissive: TERRAIN_COLORS.EDGE_HIGHLIGHT,
      emissiveIntensity: 1.5,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide,
    });

    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = this.peakHeight + 0.1;
    ring.position.z = this.halfLength * 0.3; // Near the peak
    this.mesh.add(ring);

    // Add vertical beacon at peak
    const beaconGeometry = new THREE.CylinderGeometry(0.1, 0.1, 3, 8);
    const beaconMaterial = new THREE.MeshStandardMaterial({
      color: TERRAIN_COLORS.EDGE_HIGHLIGHT,
      emissive: TERRAIN_COLORS.EDGE_HIGHLIGHT,
      emissiveIntensity: 2,
      transparent: true,
      opacity: 0.6,
    });

    const beacon = new THREE.Mesh(beaconGeometry, beaconMaterial);
    beacon.position.y = this.peakHeight + 1.5;
    beacon.position.z = this.halfLength * 0.3;
    this.mesh.add(beacon);
  }

  private createGridLines(): void {
    // Add subtle grid lines along the slope for synthwave aesthetic
    const lineMaterial = new THREE.LineBasicMaterial({
      color: TERRAIN_COLORS.GRID_LINE,
      transparent: true,
      opacity: 0.3,
    });

    // Longitudinal lines (along slope)
    const numLines = 5;
    for (let i = 0; i <= numLines; i++) {
      const localX = (i / numLines - 0.5) * this.width * 0.8;
      const points: THREE.Vector3[] = [];

      for (let z = 0; z <= 20; z++) {
        const localZ = (z / 20 - 0.5) * this.length;
        const height = this.calculateLocalHeight(localX, localZ);
        points.push(new THREE.Vector3(localX, height + 0.05, localZ));
      }

      const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(lineGeometry, lineMaterial);
      this.mesh.add(line);
    }
  }

  /**
   * Calculate height at local coordinates.
   * Uses asymmetric profile: gradual approach, steep back.
   */
  private calculateLocalHeight(localX: number, localZ: number): number {
    // Check if within bounds
    if (
      Math.abs(localX) > this.halfWidth ||
      Math.abs(localZ) > this.halfLength
    ) {
      return 0;
    }

    // Width falloff (cosine for smooth edges)
    const widthFactor = Math.cos((localX / this.halfWidth) * (Math.PI / 2));
    if (widthFactor <= 0) return 0;

    // Asymmetric length profile
    // Normalized Z: -1 = back (approach), 0 = center, +1 = front (launch edge)
    const normalizedZ = localZ / this.halfLength;

    let heightFactor: number;

    if (normalizedZ < 0) {
      // Back approach (gradual rise from back to peak at z = 0.3 * halfLength)
      // Peak is at normalizedZ = 0.3
      const approachProgress = (normalizedZ + 1) / 1.3; // 0 at back, 1 at peak
      heightFactor = Math.sin(approachProgress * Math.PI * 0.5); // Smooth rise
    } else if (normalizedZ < 0.3) {
      // Peak zone (maintain height briefly)
      heightFactor = 1;
    } else {
      // Launch edge (steeper drop-off)
      const dropProgress = (normalizedZ - 0.3) / 0.7; // 0 at peak, 1 at front edge
      heightFactor = Math.cos(dropProgress * Math.PI * 0.5); // Steep drop
    }

    return this.peakHeight * widthFactor * Math.max(0, heightFactor);
  }

  /**
   * Get height at world position.
   */
  getHeightAtPosition(worldPos: THREE.Vector3): number | null {
    const local = this.worldToLocal(worldPos);

    // Check if within bounds (with small margin)
    const margin = 1.0;
    if (
      Math.abs(local.x) > this.halfWidth + margin ||
      Math.abs(local.z) > this.halfLength + margin
    ) {
      return null;
    }

    const height = this.calculateLocalHeight(local.x, local.z);

    // Only return height if significant (above ground tolerance)
    if (height < 0.1) {
      return null;
    }

    return height;
  }

  /**
   * Get bounding box for spatial grid.
   */
  getBounds(): TerrainBounds {
    // Account for rotation in bounds calculation
    const cos = Math.abs(Math.cos(this.rotation));
    const sin = Math.abs(Math.sin(this.rotation));

    const rotatedHalfWidth = this.halfWidth * cos + this.halfLength * sin;
    const rotatedHalfLength = this.halfWidth * sin + this.halfLength * cos;

    return {
      minX: this.position.x - rotatedHalfWidth,
      maxX: this.position.x + rotatedHalfWidth,
      minZ: this.position.z - rotatedHalfLength,
      maxZ: this.position.z + rotatedHalfLength,
    };
  }
}
